import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
  OnInit,
  OnDestroy,
} from "@angular/core";
import { NgForOf, NgIf } from "@angular/common";
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatOptionModule } from "@angular/material/core";
import { MatDividerModule } from "@angular/material/divider";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";

import { CourtsService } from "../courts.service";
import { normalizeSportIdentity } from "app/shared/sport-display";
import {
  buildPricingRuleFormGroup,
  configurePricingRuleMode,
  CourtPricingRulesComponent,
  pricingRulesValidator,
  serializePricingRule,
} from "./court-pricing-rules.component";

@Component({
  selector: "app-court",
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatOptionModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatButtonToggleModule,
    MatSlideToggleModule,
    CourtPricingRulesComponent,
    NgForOf,
    NgIf,
  ],
  templateUrl: "./court.component.html",
  styleUrls: ["./court.component.scss"],
})
export class CourtComponent implements OnChanges, OnInit, OnDestroy {
  courtForm: UntypedFormGroup;
  isLoading = false;
  pictures: string[] = [];
  sportsList: any[] = [];
  selectedSport: any;
  isNameManuallyChanged = false;

  isEditMode = false;

  @Input() data: any;
  @Input() venueId: string;
  @Output() clicked = new EventEmitter<any>();
  @ViewChild("fileInput") fileInput!: ElementRef<HTMLInputElement>;

  originalPictures: string[] = [];
  removePictures: string[] = [];
  selectedFiles: File[] = [];

  private localCourtState: {
    formData: any;
    pictures: string[];
    selectedFiles: File[];
    removePictures: string[];
  } | null = null;

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private courtService: CourtsService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["data"]) {
      this.isEditMode = !!this.data?.id;
      this.initializeForm();
    }
  }

  ngOnInit(): void {
    this.isEditMode = !!this.data?.id;
    this.initializeForm();
    this.loadSports();
  }

  private loadSports(): void {
    this.courtService.getAllSports().subscribe({
      next: (response) => {
        this.sportsList = response.results || [];

        if (this.isEditMode && this.data?.sports) {
          // Editing → preselect existing sport
          const existingSportIdentity = normalizeSportIdentity(
            this.data.sports,
          );
          this.selectedSport = this.sportsList.find((sport) =>
            [sport.key, sport.name, sport.code].some(
              (value) =>
                normalizeSportIdentity(value) === existingSportIdentity,
            ),
          );
          this.courtForm.patchValue({
            sports: this.selectedSport?.key ?? this.data.sports,
          });
        } else if (!this.isEditMode && this.sportsList.length > 0) {
          // New form → pick first sport automatically
          this.selectedSport = this.sportsList[0];
          this.courtForm.patchValue({ sports: this.sportsList[0].key });
        }
      },
      error: (error) => {
        console.error("Error loading sports:", error);
        this.snackBar.open("Error loading sports", "Close", {
          duration: 3000,
          panelClass: ["error-snackbar"],
        });
      },
    });
  }

  onSportChange(sportKey: string): void {
    this.selectedSport = this.sportsList.find(
      (sport) => sport.key === sportKey,
    );

    // Only auto-set status for NEW courts, not when editing
    if (this.selectedSport && !this.isEditMode) {
      const status = this.selectedSport.active ? "active" : "inactive";
      this.courtForm.patchValue({ status });
    }
  }

  onNameInputChange(): void {
    this.isNameManuallyChanged = true;
  }

  private initializeForm(): void {
    if (this.isEditMode && this.data) {
      this.courtForm = this._formBuilder.group({
        name: [this.data.name, Validators.required],
        sports: [this.data.sports],
        status: [this.data.status],
        courtType: [this.data.courtType],
        surfaceType: [this.data.surfaceType],
        airConditioned: [this.data.airConditioned],
        maxCapacity: [
          this.data.maxCapacity,
          [
            Validators.min(1),
            Validators.max(100),
            Validators.required,
            Validators.pattern("^[0-9]*$"),
          ],
        ],
        minCapacity: [
          this.data.minCapacity,
          [
            Validators.min(1),
            Validators.max(52),
            Validators.required,
            Validators.pattern("^[0-9]*$"),
          ],
        ],
        pricePerHour: [
          this.data.pricePerHour,
          [Validators.min(0), Validators.required],
        ],
        timeBasedPricing: [
          Array.isArray(this.data.pricingRules) &&
            this.data.pricingRules.length > 0,
        ],
        pricingRules: this._formBuilder.array(
          (this.data.pricingRules || []).map((rule: any) =>
            buildPricingRuleFormGroup(this._formBuilder, rule),
          ),
          pricingRulesValidator,
        ),
      });

      this.originalPictures = this.data.pictures || [];
      this.pictures = [...this.originalPictures];
      this.selectedFiles = [];
      this.removePictures = [];
      this.isNameManuallyChanged = true;
    } else if (!this.isEditMode && this.localCourtState) {
      this.courtForm = this._formBuilder.group({
        name: [this.localCourtState.formData.name || "", Validators.required],
        sports: [this.localCourtState.formData.sports || ""],
        status: [this.localCourtState.formData.status || "active"],
        courtType: [this.localCourtState.formData.courtType || "outdoor"],
        surfaceType: [this.localCourtState.formData.surfaceType || "grass"],
        airConditioned: [this.localCourtState.formData.airConditioned ?? true],
        maxCapacity: [
          this.localCourtState.formData.maxCapacity || null,
          [
            Validators.min(1),
            Validators.max(100),
            Validators.required,
            Validators.pattern("^[0-9]*$"),
          ],
        ],
        minCapacity: [
          this.localCourtState.formData.minCapacity || null,
          [
            Validators.min(1),
            Validators.max(52),
            Validators.required,
            Validators.pattern("^[0-9]*$"),
          ],
        ],
        pricePerHour: [
          this.localCourtState.formData.pricePerHour || null,
          [Validators.min(0), Validators.required],
        ],
        timeBasedPricing: [
          this.localCourtState.formData.timeBasedPricing || false,
        ],
        pricingRules: this._formBuilder.array(
          (this.localCourtState.formData.pricingRules || []).map((rule: any) =>
            buildPricingRuleFormGroup(this._formBuilder, rule),
          ),
          pricingRulesValidator,
        ),
      });

      this.originalPictures = [];
      this.pictures = [...this.localCourtState.pictures];
      this.selectedFiles = [...this.localCourtState.selectedFiles];
      this.removePictures = [...this.localCourtState.removePictures];
      this.isNameManuallyChanged = !!this.localCourtState.formData.name;
    } else {
      this.courtForm = this._formBuilder.group({
        name: ["", Validators.required],
        sports: [""],
        status: ["active"],
        courtType: ["outdoor"],
        surfaceType: ["grass"],
        airConditioned: [true],
        maxCapacity: [
          null,
          [
            Validators.min(1),
            Validators.max(100),
            Validators.required,
            Validators.pattern("^[0-9]*$"),
          ],
        ],
        minCapacity: [
          null,
          [
            Validators.min(1),
            Validators.max(52),
            Validators.required,
            Validators.pattern("^[0-9]*$"),
          ],
        ],
        pricePerHour: [null, [Validators.min(0), Validators.required]],
        timeBasedPricing: [false],
        pricingRules: this._formBuilder.array([], pricingRulesValidator),
      });

      this.originalPictures = [];
      this.pictures = [];
      this.selectedFiles = [];
      this.removePictures = [];
      this.localCourtState = null;
      this.isNameManuallyChanged = false;
    }

    this.courtForm.get("sports")?.valueChanges.subscribe((value) => {
      this.onSportChange(value);
    });
    this.attachCapacityValidator();
    this.attachPricingToggle();
  }

  get pricingRules(): UntypedFormArray {
    return this.courtForm.get("pricingRules") as UntypedFormArray;
  }

  private attachPricingToggle(): void {
    const toggle = this.courtForm.get("timeBasedPricing");
    const rules = this.pricingRules;
    const applyState = (enabled: boolean) => {
      if (!enabled) {
        rules.disable({ emitEvent: false });
        return;
      }
      rules.enable({ emitEvent: false });
      rules.controls.forEach((rule) =>
        configurePricingRuleMode(rule, rule.get("intervalMode")?.value),
      );
    };
    applyState(!!toggle?.value);
    toggle?.valueChanges.subscribe(applyState);
  }

  private saveLocalState(): void {
    if (!this.isEditMode) {
      this.localCourtState = {
        formData: this.courtForm.getRawValue(),
        pictures: [...this.pictures],
        selectedFiles: [...this.selectedFiles],
        removePictures: [...this.removePictures],
      };
    }
  }

  openFilePicker() {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    if (!files?.length) return;

    for (let i = 0; i < files.length; i++) {
      const f = files.item(i)!;
      if (!f.type.startsWith("image/")) {
        this.snackBar.open("Please select only image files", "Close", {
          duration: 3000,
          panelClass: ["error-snackbar"],
        });
        continue;
      }

      this.selectedFiles.push(f);
      const preview = URL.createObjectURL(f);
      this.pictures.push(preview);
    }

    event.target.value = "";
    this.saveLocalState();
  }

  removePicture(index: number) {
    if (index < 0 || index >= this.pictures.length) return;

    const pictureUrl = this.pictures[index];
    const selectedFileIndex = this.pictures
      .slice(0, index)
      .filter((picture) => picture.startsWith("blob:")).length;
    this.pictures.splice(index, 1);

    if (pictureUrl.startsWith("blob:")) {
      this.selectedFiles.splice(selectedFileIndex, 1);
      URL.revokeObjectURL(pictureUrl);
    } else {
      if (
        this.originalPictures.includes(pictureUrl) &&
        !this.removePictures.includes(pictureUrl)
      ) {
        this.removePictures.push(pictureUrl);
      }
    }

    this.saveLocalState();
  }

  onSubmit(): void {
    if (this.courtForm.invalid || this.isLoading) return;
    if (!this.venueId) {
      this.snackBar.open("Missing venue id", "Close", {
        duration: 3000,
        panelClass: ["error-snackbar"],
      });
      return;
    }

    this.isLoading = true;

    const formData = new FormData();
    formData.append("name", this.courtForm.value.name);
    formData.append("sports", this.courtForm.value.sports);
    formData.append("status", this.courtForm.value.status);
    formData.append("courtType", this.courtForm.value.courtType);
    formData.append("surfaceType", this.courtForm.value.surfaceType);
    formData.append("airConditioned", this.courtForm.value.airConditioned);
    formData.append("minCapacity", this.courtForm.value.minCapacity);
    formData.append("maxCapacity", this.courtForm.value.maxCapacity);
    formData.append("pricePerHour", this.courtForm.value.pricePerHour);
    const pricingRules = this.courtForm.get("timeBasedPricing")?.value
      ? this.pricingRules.controls.map(serializePricingRule)
      : [];
    formData.append("pricingRules", JSON.stringify(pricingRules));

    this.selectedFiles.forEach((file) => {
      formData.append("pictures", file);
    });
    this.removePictures.forEach((pic) => {
      formData.append("removePictures", pic);
    });
    if (this.isEditMode && this.data.id) {
      formData.append("id", this.data.id);
    }

    this.courtService.createOrUpdateCourt(this.venueId, formData).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.snackBar.open(
          `Court ${this.isEditMode ? "updated" : "created"} successfully!`,
          "Close",
          { duration: 3000, panelClass: ["success-snackbar"] },
        );

        this.cleanupBlobUrls();

        if (response) {
          if (!this.isEditMode) {
            this.data = response;
            this.isEditMode = true;
            this.localCourtState = null;
          } else {
            this.data = { ...this.data, ...response };
          }

          this.originalPictures = response.pictures || [];
          this.pictures = [...this.originalPictures];
        }

        this.selectedFiles = [];
        this.removePictures = [];
        if (this.fileInput?.nativeElement)
          this.fileInput.nativeElement.value = "";

        this.clicked.emit({
          action: this.isEditMode ? "updated" : "created",
          data: response || this.data,
        });
      },
      error: (error) => {
        this.isLoading = false;
        this.snackBar.open("Error creating court. Please try again.", "Close", {
          duration: 3000,
          panelClass: ["error-snackbar"],
        });
        console.error("Error creating/updating court:", error);
        this.restorePictures();
      },
    });
  }

  private cleanupBlobUrls(): void {
    this.pictures.forEach((picture) => {
      if (picture && picture.startsWith("blob:")) {
        URL.revokeObjectURL(picture);
      }
    });
  }

  private restorePictures(): void {
    if (this.isEditMode) {
      this.pictures = [...this.originalPictures];
    } else if (this.localCourtState) {
      this.pictures = [...this.localCourtState.pictures];
    } else {
      this.pictures = [];
    }
    this.selectedFiles = [];
    this.removePictures = [];
    this.cleanupBlobUrls();
  }

  resetForm(): void {
    this.localCourtState = null;
    this.isNameManuallyChanged = false;
    this.initializeForm();
  }

  ngOnDestroy(): void {
    this.cleanupBlobUrls();
  }

  trackByPicture(index: number, picture: string): string {
    return picture || index.toString();
  }

  private attachCapacityValidator(): void {
    const minCtrl = this.courtForm.get("minCapacity");
    const maxCtrl = this.courtForm.get("maxCapacity");

    if (!minCtrl || !maxCtrl) return;

    const validate = () => {
      const minRaw = minCtrl.value;
      const maxRaw = maxCtrl.value;

      // Only run range check if both fields pass their own validators
      const minHasLocalErrors = !!minCtrl.errors;
      const maxHasLocalErrors =
        !!maxCtrl.errors && !maxCtrl.errors["capacityRange"];

      const min = Number(minRaw);
      const max = Number(maxRaw);

      const shouldCheck =
        !minHasLocalErrors &&
        !maxHasLocalErrors &&
        minRaw !== null &&
        maxRaw !== null &&
        minRaw !== "" &&
        maxRaw !== "" &&
        !Number.isNaN(min) &&
        !Number.isNaN(max);

      const capacityInvalid = shouldCheck && min > max;

      // Set or clear error on maxCapacity without clobbering other errors
      const prevMaxErrors = maxCtrl.errors || {};
      if (capacityInvalid) {
        maxCtrl.setErrors({ ...prevMaxErrors, capacityRange: true });
      } else if ("capacityRange" in prevMaxErrors) {
        const { capacityRange, ...rest } = prevMaxErrors;
        maxCtrl.setErrors(Object.keys(rest).length ? rest : null);
      }

      // Mirror error at the group level (optional, for form-level states)
      const prevGroupErrors = this.courtForm.errors || {};
      if (capacityInvalid) {
        this.courtForm.setErrors({ ...prevGroupErrors, capacityRange: true });
      } else if ("capacityRange" in prevGroupErrors) {
        const { capacityRange, ...rest } = prevGroupErrors;
        this.courtForm.setErrors(Object.keys(rest).length ? rest : null);
      }
    };

    // Revalidate on changes
    minCtrl.valueChanges.subscribe(validate);
    maxCtrl.valueChanges.subscribe(validate);

    // Initial run
    validate();
  }
}
